import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useOrganizationList, useUser } from "@clerk/react";
import { ArrowUpRightIcon, ChevronRightIcon, CircleAlertIcon, CircleCheckIcon, RefreshCwIcon } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Frame, FrameDescription, FrameHeader, FramePanel, FrameTitle } from "@/components/ui/frame";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { RelativeTime } from "@/components/dashboard_sourcing/RelativeTime";
import { useOpsOverview } from "./useOpsOverview";
import { useOpsQueue } from "./useOpsQueue";
import { ClientCard } from "./ClientCard";
import { ClientDetailsSheet } from "./ClientDetails";
import { alertText, fmtAge } from "./format";
import { SeverityIcon } from "./parts";
import { ActionsFrame, CapabilitiesFrame, DecisionsFrame, SourcingFrame, WritesBadge } from "./Queue";
import type { ClientState, HandoffState, OpsOverviewResponse } from "./types";

/**
 * Staff-only Success board (/manager/success): one card per managed client
 * from the Success controller's hourly snapshots (amy/lib/ops — docs/
 * success-controller.md), what needs attention across clients, the decisions
 * queue with Accept / Snooze / Dismiss, the cross-org list of open handoffs,
 * then the sourcing requests, the actions log and the capabilities panel
 * (Queue.tsx). The ONE write is resolving a decision; while amy runs with
 * OPS_WRITE_ACTIONS off an Accept is recorded as "would do" and nothing is
 * called — the header says which state applies. "Open Tasks" switches the
 * dashboard to that client's org and opens its Tasks page, where the handoff
 * is answered as before.
 */
export default function SuccessPage() {
  const { user, isLoaded } = useUser();
  const isStaff = Boolean((user?.publicMetadata as Record<string, unknown> | undefined)?.uplodio_member);
  const { data, error, loading, fetchedAt, refresh } = useOpsOverview(isLoaded && isStaff);
  const queue = useOpsQueue(isLoaded && isStaff);

  if (!isLoaded) return null;
  if (!isStaff) {
    return <div className="flex h-full items-center justify-center p-6 text-sm text-muted-foreground">This page is for Uplodio staff only.</div>;
  }
  return <SuccessBoard data={data} error={error} loading={loading} fetchedAt={fetchedAt} queue={queue} onRecompute={() => void refresh(true).then(() => queue.refresh())} />;
}

type OpsQueueState = ReturnType<typeof useOpsQueue>;

export function SuccessBoard({
  data,
  error,
  loading,
  fetchedAt,
  queue,
  onRecompute,
}: {
  data: OpsOverviewResponse | null;
  error: string | null;
  loading: boolean;
  fetchedAt: number | null;
  queue: OpsQueueState;
  onRecompute: () => void;
}) {
  // The slug outlives `open` so the sheet keeps its content while it slides out.
  const [sheet, setSheet] = useState<{ slug: string | null; open: boolean }>({ slug: null, open: false });
  // Links from the Slack push: ?decision=<id> opens that decision, #handoffs jumps to the handoff list.
  const [params] = useSearchParams();
  const focusId = params.get("decision");
  const clients = data?.clients ?? [];
  const openClient = (slug: string) => setSheet({ slug, open: true });
  const clientNames = new Map(clients.map((c) => [c.slug, c.name] as const));
  const writes = queue.data?.capabilities.writes_enabled ?? null;

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-4 md:p-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            Success
            <WritesBadge writes={writes} />
          </h1>
          <p className="text-sm text-muted-foreground">Managed clients against their goals and what the rules propose, computed hourly by Amy.</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">
            {data?.computed_at ? (
              <>
                Snapshot <RelativeTime at={data.computed_at} />
              </>
            ) : loading ? (
              "Loading…"
            ) : (
              "No snapshot yet"
            )}
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={onRecompute}
            disabled={loading}
            title={`Recompute every client now: snapshots, rules and the decisions queue (reads Amy's data, writes only the ops collection; never starts a model session)${fetchedAt ? ` — last read ${new Date(fetchedAt).toLocaleTimeString()}` : ""}`}
          >
            <RefreshCwIcon className={cn(loading && "animate-spin")} />
            Recompute
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTitle><CircleAlertIcon />Could not load the board</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {queue.error && (
        <Alert variant="warning">
          <AlertTitle><CircleAlertIcon />Could not load the decisions queue</AlertTitle>
          <AlertDescription>{queue.error}</AlertDescription>
        </Alert>
      )}

      {!data && loading && (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          <Skeleton className="h-72 rounded-xl" />
          <Skeleton className="h-72 rounded-xl" />
          <Skeleton className="h-72 rounded-xl" />
        </div>
      )}

      {data && clients.length === 0 && (
        <Alert variant="info">
          <AlertTitle><CircleAlertIcon />No managed clients</AlertTitle>
          <AlertDescription>No client plan has been swept yet. The hourly sweep writes the first snapshot; Recompute does it now.</AlertDescription>
        </Alert>
      )}

      {clients.length > 0 && (
        <>
          <Attention clients={clients} onOpen={openClient} />
          {queue.data && (
            <DecisionsFrame
              decisions={queue.data.decisions}
              clientNames={clientNames}
              writes={queue.data.capabilities.writes_enabled}
              sessionsOn={queue.data.capabilities.judgment.runs_while_writes_off}
              resolving={queue.resolving}
              lastAction={queue.lastAction}
              onResolve={queue.resolve}
              focusId={focusId}
            />
          )}
          <section className="flex flex-col gap-2">
            <h2 className="text-sm font-medium text-muted-foreground">Clients · {clients.length}</h2>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {clients.map((client) => (
                <ClientCard key={client.slug} client={client} onOpen={() => openClient(client.slug)} />
              ))}
            </div>
          </section>
          <Handoffs clients={clients} />
          {queue.data && (
            <>
              <SourcingFrame requests={queue.data.sourcing} clientNames={clientNames} />
              <ActionsFrame actions={queue.data.actions} clientNames={clientNames} />
              <CapabilitiesFrame capabilities={queue.data.capabilities} clientNames={clientNames} />
            </>
          )}
        </>
      )}

      <ClientDetailsSheet
        client={clients.find((c) => c.slug === sheet.slug) ?? null}
        open={sheet.open}
        onOpenChange={(open) => setSheet((s) => ({ ...s, open }))}
      />
    </div>
  );
}

const LEVEL_RANK = { critical: 0, warn: 1, info: 2 } as const;

/** Every warning and critical alert across clients, worst first; a row opens that client's details. */
function Attention({ clients, onOpen }: { clients: ClientState[]; onOpen: (slug: string) => void }) {
  const items = clients
    .flatMap((client) => client.alerts.filter((a) => a.level !== "info").map((alert) => ({ client, alert })))
    .sort((a, b) => LEVEL_RANK[a.alert.level] - LEVEL_RANK[b.alert.level]);

  if (items.length === 0) {
    return (
      <Alert variant="success">
        <AlertTitle>
          <CircleCheckIcon />
          Nothing needs attention right now.
        </AlertTitle>
      </Alert>
    );
  }

  const critical = items.some((i) => i.alert.level === "critical");
  return (
    <Frame>
      <FrameHeader className="flex-row items-center gap-2">
        <FrameTitle>Needs attention</FrameTitle>
        <Badge variant={critical ? "error" : "warning"}>{items.length}</Badge>
      </FrameHeader>
      <FramePanel className="@container overflow-hidden p-0">
        <ul className="divide-y">
          {items.map(({ client, alert }, i) => (
            <li key={`${client.slug}-${alert.code}-${i}`}>
              <button
                type="button"
                onClick={() => onOpen(client.slug)}
                className="flex w-full cursor-pointer items-start gap-3 px-4 py-2.5 text-left text-sm outline-none hover:bg-muted/50 focus-visible:bg-muted/50"
              >
                <SeverityIcon level={alert.level} className="mt-0.5" />
                <span className="flex min-w-0 flex-1 flex-col gap-0.5 @lg:flex-row @lg:gap-3">
                  <span className="shrink-0 truncate font-medium @lg:w-36">{client.name}</span>
                  <span className="min-w-0 flex-1 text-muted-foreground">{alertText(client, alert)}</span>
                </span>
                <ChevronRightIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground/60" />
              </button>
            </li>
          ))}
        </ul>
      </FramePanel>
    </Frame>
  );
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function Handoffs({ clients }: { clients: ClientState[] }) {
  // The board renders after its data loads, too late for the browser's own jump to #handoffs.
  const { hash } = useLocation();
  const frameRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (hash === "#handoffs") frameRef.current?.scrollIntoView({ block: "start" });
  }, [hash]);
  const navigate = useNavigate();
  const { setActive } = useOrganizationList();
  const [switching, setSwitching] = useState<string | null>(null);
  const handoffs = clients
    .flatMap((client) => client.handoffs.map((h) => ({ ...h, client })))
    .sort((a, b) => (b.age_hours ?? -1) - (a.age_hours ?? -1));

  const openTasks = async (h: HandoffState & { client: ClientState }) => {
    if (!h.client.org_external_id || !setActive) return;
    setSwitching(h.campaign_creator_id);
    try {
      await setActive({ organization: h.client.org_external_id });
      navigate("/dashboard/tasks");
    } finally {
      setSwitching(null);
    }
  };

  return (
    <Frame id="handoffs" ref={frameRef} className="scroll-mt-4">
      <FrameHeader className="flex-row flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <div className="flex items-center gap-2">
          <FrameTitle>Open handoffs</FrameTitle>
          {handoffs.length > 0 && <Badge variant="ghost">{handoffs.length}</Badge>}
        </div>
        <FrameDescription>Questions Amy could not answer, oldest first</FrameDescription>
      </FrameHeader>
      <FramePanel className="@container overflow-hidden p-0">
        {handoffs.length === 0 ? (
          <p className="px-4 py-3 text-sm text-muted-foreground">No open handoffs.</p>
        ) : (
          <ul className="divide-y">
            {handoffs.map((h) => (
              <li key={h.campaign_creator_id} className="flex flex-col gap-2 px-4 py-3 @lg:flex-row @lg:items-center @lg:gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span className="text-sm font-medium">{h.creator}</span>
                    <span className="text-xs text-muted-foreground">
                      {h.client.name} · {h.campaign_name}
                      {UUID.test(h.status) ? "" : ` · ${h.status}`}
                    </span>
                  </div>
                  <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground" title={h.question ?? undefined}>
                    {h.question ?? <span className="italic">No question recorded</span>}
                  </p>
                </div>
                <div className="flex shrink-0 items-center justify-between gap-3 @lg:justify-end">
                  <span
                    className={cn("font-mono text-xs", (h.age_hours ?? 0) >= 24 ? "text-warning" : "text-muted-foreground")}
                    title="Waiting since the controller first saw it"
                  >
                    {fmtAge(h.age_hours)}
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={!h.client.org_external_id || switching === h.campaign_creator_id}
                    onClick={() => void openTasks(h)}
                    title={h.client.org_external_id ? "Switch to this client's org and open its Tasks page" : "Org not mapped"}
                  >
                    Open Tasks
                    <ArrowUpRightIcon />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </FramePanel>
    </Frame>
  );
}
